import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Send,
  ChevronDown,
} from "lucide-react";
import { siteConfig } from "../config/siteConfig";
import { toast, Toaster } from "sonner";

export const Contato = () => {
  const { contact, faq } = siteConfig;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
    message: "",
  });
  const [openFaq, setOpenFaq] = useState(0);
  const waUrl = `https://wa.me/${contact.whatsappRaw}?text=${encodeURIComponent(
    contact.whatsappMessage,
  )}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Por favor, preencha nome e telefone.");
      return;
    }
    // Envia a mensagem via WhatsApp (estático — sem backend)
    const text = `Olá! Meu nome é ${form.name}.
Telefone: ${form.phone}
Serviço: ${form.service || "Não especificado"}

${form.message || "Gostaria de um orçamento."}`;
    const url = `https://wa.me/${contact.whatsappRaw}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    toast.success("Abrindo WhatsApp com sua mensagem...");
    setForm({ name: "", phone: "", service: "", message: "" });
  };

  return (
    <div data-testid="contato-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <section className="bg-[#f8fafc] border-b border-slate-200">
        <div className="container-site py-16 md:py-24">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-blue-700">
              Home
            </Link>
            <span>/</span>
            <span className="text-[#0f2a4a] font-semibold">Contato</span>
          </div>
          <div className="eyebrow mb-3">Fale conosco</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#0f2a4a] tracking-tight max-w-4xl leading-[1.05]">
            Pronto para atender você agora mesmo.
          </h1>
          <p className="mt-6 text-slate-600 max-w-2xl leading-relaxed">
            Ligue, mande no WhatsApp ou preencha o formulário. Respondemos em
            minutos.
          </p>
        </div>
      </section>

      {/* Contact grid */}
      <section className="py-20 md:py-28">
        <div className="container-site grid md:grid-cols-5 gap-10">
          {/* Info cards */}
          <div className="md:col-span-2 space-y-4">
            <a
              href={`tel:${contact.phoneRaw}`}
              className="block bg-white border border-slate-200 p-6 rounded-sm hover:border-green-300 hover:shadow-md transition group"
              data-testid="contato-phone-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f2a4a] text-green-500 rounded-sm inline-flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-slate-400">Telefone</div>
                  <div className="text-lg font-bold text-[#0f2a4a] mt-1">
                    {contact.phone}
                  </div>
                </div>
              </div>
            </a>

            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="block bg-white border border-slate-200 p-6 rounded-sm hover:border-green-300 hover:shadow-md transition group"
              data-testid="contato-whatsapp-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f2a4a] text-green-500 rounded-sm inline-flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-slate-400">WhatsApp</div>
                  <div className="text-lg font-bold text-[#0f2a4a] mt-1">
                    {contact.phone}
                  </div>
                </div>
              </div>
            </a>

            <a
              href={`mailto:${contact.email}`}
              className="block bg-white border border-slate-200 p-6 rounded-sm hover:border-green-300 hover:shadow-md transition group"
              data-testid="contato-email-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f2a4a] text-green-500 rounded-sm inline-flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-slate-400">E-mail</div>
                  <div className="text-base font-bold text-[#0f2a4a] mt-1 break-all">
                    {contact.email}
                  </div>
                </div>
              </div>
            </a>

            <div className="bg-white border border-slate-200 p-6 rounded-sm">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f2a4a] text-green-500 rounded-sm inline-flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-slate-400">Endereço</div>
                  <div className="text-sm text-[#0f2a4a] mt-1 font-medium">
                    {contact.address}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6 rounded-sm">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0f2a4a] text-green-500 rounded-sm inline-flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="eyebrow text-slate-400">Atendimento</div>
                  <div className="text-sm text-[#0f2a4a] mt-1 font-bold">
                    {contact.workingHours}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-3">
            <div className="bg-[#0f2a4a] text-white p-8 md:p-10 rounded-sm">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">
                Peça seu orçamento
              </h2>
              <p className="mt-2 text-slate-300 text-sm">
                Preencha e envie pelo WhatsApp. Respondemos em minutos.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-7 space-y-5"
                data-testid="contato-form"
              >
                <div>
                  <label className="text-xs font-bold tracking-wider uppercase text-slate-300">
                    Nome*
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/20 rounded-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-green-500 focus:bg-white/10 transition"
                    placeholder="Seu nome"
                    data-testid="form-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-bold tracking-wider uppercase text-slate-300">
                      Telefone*
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/20 rounded-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-green-500 focus:bg-white/10 transition"
                      placeholder="(11) 99999-9999"
                      data-testid="form-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold tracking-wider uppercase text-slate-300">
                      Serviço
                    </label>
                    <select
                      value={form.service}
                      onChange={(e) =>
                        setForm({ ...form, service: e.target.value })
                      }
                      className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/20 rounded-sm text-white focus:outline-none focus:border-green-500 focus:bg-white/10 transition appearance-none"
                      data-testid="form-service"
                    >
                      <option value="" className="bg-[#0f2a4a]">
                        Selecione...
                      </option>
                      {siteConfig.services.map((s) => (
                        <option
                          key={s.title}
                          value={s.title}
                          className="bg-[#0f2a4a]"
                        >
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold tracking-wider uppercase text-slate-300">
                    Mensagem
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    rows={4}
                    className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/20 rounded-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-green-500 focus:bg-white/10 transition resize-none"
                    placeholder="Descreva o seu problema..."
                    data-testid="form-message"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-600 text-white font-bold rounded-sm transition shadow-lg"
                  data-testid="form-submit"
                >
                  <Send className="w-4 h-4" />
                  Enviar pelo WhatsApp
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="container-site max-w-4xl">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">Perguntas Frequentes</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">
              Dúvidas comuns dos nossos clientes
            </h2>
          </div>
          <div className="space-y-3">
            {faq.map((item, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-slate-50 transition"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-bold text-[#0f2a4a] text-base md:text-lg">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-blue-700 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;
